import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { Communications } from "@/src/gql/graphql";

/**
 * Paginated communications hook with field-specific search parameters
 * Enables server-side filtering and pagination for the communications table
 *
 * Supports:
 * - Field-specific searches: titleSearch, bodySearch, contactSearch
 * - Type and direction filtering
 * - Date range filtering on occurred_at
 * - Pagination with total count
 */
export function useCommunicationsPaginated(params?: {
  page?: number;
  limit?: number;
  search?: string;
  titleSearch?: string;
  bodySearch?: string;
  contactSearch?: string;
  typeSearch?: string;
  direction?: "inbound" | "outbound";
  status?: string | string[];
  dateRangeStart?: string;
  dateRangeEnd?: string;
}) {
  return useQuery({
    queryKey: ["communications-paginated", params],
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: async () => {
      console.log("🔍 useCommunicationsPaginated: queryFn start", { params });
      const supabase = getSupabase();

      // Build base query for filtering
      let countQuery = supabase.from("communications").select("*", { count: "exact", head: true });
      let dataQuery = supabase.from("communications").select("*");

      // Apply exact match filters
      if (params?.direction) {
        countQuery = countQuery.eq("direction", params.direction);
        dataQuery = dataQuery.eq("direction", params.direction);
      }

      if (params?.status) {
        if (Array.isArray(params.status) && params.status.length > 0) {
          countQuery = countQuery.in("status", params.status);
          dataQuery = dataQuery.in("status", params.status);
        } else if (typeof params.status === "string") {
          countQuery = countQuery.eq("status", params.status);
          dataQuery = dataQuery.eq("status", params.status);
        }
      }

      if (params?.typeSearch) {
        countQuery = countQuery.eq("type_id", params.typeSearch);
        dataQuery = dataQuery.eq("type_id", params.typeSearch);
      }

      // Apply date range filters
      if (params?.dateRangeStart) {
        countQuery = countQuery.gte("occurred_at", params.dateRangeStart);
        dataQuery = dataQuery.gte("occurred_at", params.dateRangeStart);
      }

      if (params?.dateRangeEnd) {
        countQuery = countQuery.lte("occurred_at", params.dateRangeEnd);
        dataQuery = dataQuery.lte("occurred_at", params.dateRangeEnd);
      }

      // Handle general search (backward compatibility)
      if (params?.search) {
        const searchFilter = `title.ilike.%${params.search}%,body.ilike.%${params.search}%`;
        countQuery = countQuery.or(searchFilter);
        dataQuery = dataQuery.or(searchFilter);
      }

      // Handle specific field searches - build OR conditions for each field
      const specificSearchConditions = [];
      if (params?.titleSearch) {
        specificSearchConditions.push(`title.ilike.%${params.titleSearch}%`);
      }
      if (params?.bodySearch) {
        specificSearchConditions.push(`body.ilike.%${params.bodySearch}%`);
      }
      if (params?.contactSearch) {
        // Search in contact name/email via join
        specificSearchConditions.push(
          `contact_id.in.(select id from contacts where name.ilike.%${params.contactSearch}% or email.ilike.%${params.contactSearch}%)`
        );
      }

      // Apply specific search conditions if any exist
      if (specificSearchConditions.length > 0) {
        const specificSearchFilter = specificSearchConditions.join(",");
        countQuery = countQuery.or(specificSearchFilter);
        dataQuery = dataQuery.or(specificSearchFilter);
      }

      // Get total count
      const { count, error: countError } = await countQuery;
      if (countError) {
        console.error("Error fetching communications count:", countError);
        throw new Error(countError.message);
      }

      // Apply sorting
      dataQuery = dataQuery.order("occurred_at", { ascending: false });

      // Apply pagination
      if (params?.limit) {
        const offset = (params.page || 0) * params.limit;
        dataQuery = dataQuery.range(offset, offset + params.limit - 1);
      }

      // Fetch data
      const { data, error } = await dataQuery;
      if (error) {
        console.error("Error fetching communications:", error);
        throw new Error(error.message);
      }

      const total = count || 0;
      const pageCount = params?.limit ? Math.ceil(total / params.limit) : 1;

      console.log("✅ useCommunicationsPaginated: queryFn complete", {
        dataLength: data?.length || 0,
        total,
        pageCount,
      });

      return {
        data: (data || []) as Communications[],
        total,
        pageCount,
        pageInfo: {
          hasNextPage: params?.limit ? (params.page || 0) < pageCount - 1 : false,
          hasPreviousPage: (params?.page || 0) > 0,
        },
      };
    },
  });
}
